import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, UploadCloud } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/toast";
import { Dropdown } from "../../components/ui/dropdown";
import { DateTimePicker } from "../../components/ui/date-picker";
import { UploadContactBatchModal } from "../workflows/upload-contact-batch-modal";
import { listContactBatches } from "../workflows/api";
import { chatbotApi } from "../chatbot/chatbot-api";
import { campaignsApi, templatesApi } from "./api";
import { cn } from "../../lib/utils";

const label = "mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3";

export interface CampaignPreset {
  campaign?: string;
  messageKind?: "template" | "ai_agent";
  templateId?: string;
  agentId?: string;
  instanceName?: string;
  concurrency?: number;
}

export function WhatsAppCampaignScheduler({
  onCancel,
  onComplete,
  initialPreset,
}: {
  onCancel?: () => void;
  onComplete?: () => void;
  initialPreset?: CampaignPreset | null;
} = {}) {
  const { token } = useAuth() as any;
  const toast = useToast();
  const cache = useQueryClient();

  const [searchParams] = useSearchParams();
  const [showUpload, setShowUpload] = useState(false);
  // Pre-selected when arriving from a batch's "Schedule WhatsApp Campaign"
  // action (CRM Batches / Leads pages) — same ?batchId= convention the
  // workflow scheduler uses.
  const [batchId, setBatchId] = useState(() => searchParams.get("batchId") || "");
  const [messageKind, setMessageKind] = useState<"template" | "ai_agent">(
    () => initialPreset?.messageKind || "template"
  );
  const [templateId, setTemplateId] = useState(() => initialPreset?.templateId || "");
  const [agentId, setAgentId] = useState(() => initialPreset?.agentId || "");
  const [instanceName, setInstanceName] = useState(() => initialPreset?.instanceName || "");
  const [campaign, setCampaign] = useState(() => initialPreset?.campaign || "");
  const [mode, setMode] = useState<"now" | "later">("now");
  const [at, setAt] = useState("");
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [concurrency, setConcurrency] = useState(() => initialPreset?.concurrency || 20);

  const batches = useQuery({ queryKey: ["contact-batches"], queryFn: listContactBatches, enabled: Boolean(token) });
  const templates = useQuery({ queryKey: ["whatsapp-templates", token], queryFn: templatesApi.list, enabled: Boolean(token) });
  const agents = useQuery({ queryKey: ["chatbot-agents", token], queryFn: () => chatbotApi.agents(token), enabled: Boolean(token) });
  const instances = useQuery({
    queryKey: ["chatbot-instances", token],
    queryFn: () => chatbotApi.instances(token),
    enabled: Boolean(token),
  });


  // CRM batches are launchable here too (the "Schedule WhatsApp Campaign"
  // action on the Batches/Leads pages), not just ones uploaded directly on
  // this page — same unfiltered-by-source approach the workflow scheduler uses.
  const waBatches = (batches.data ?? []).filter((b: any) => (b.source === "whatsapp" || b.source === "crm") && !b.archived);
  const past = mode === "later" && !!at && new Date(at) <= new Date();
  // Only Meta-approved templates can be attached: a Cloud-API number refuses
  // anything else outside the 24-hour window, per recipient. The server
  // enforces the same rule, so this filter can never hide a working option.
  const approvedTemplates = (templates.data ?? []).filter(
    t => !!t.template_id && String(t.status || "draft").toLowerCase() === "approved",
  );
  const pickedTemplate = approvedTemplates.find(t => t.template_id === templateId);
  const senderForTemplate = (pickedTemplate?.submitted_instance_name || "").trim();

  const launch = useMutation({
    mutationFn: () =>
      campaignsApi.create({
        batch_id: batchId,
        message_kind: messageKind,
        template_id: messageKind === "template" ? templateId : undefined,
        agent_id: messageKind === "ai_agent" ? agentId : undefined,
        instance_name: instanceName,
        mode,
        scheduled_at: mode === "later" ? at : undefined,
        timezone,
        concurrency,
        campaign: campaign.trim() || undefined,
      }),
    onSuccess: (data) => {
      toast.success(mode === "later" ? "WhatsApp campaign scheduled" : "WhatsApp campaign launched", `Sending to batch via ${data.instance_name}.`);
      cache.invalidateQueries({ queryKey: ["whatsapp-campaigns", token] });
      setBatchId(""); setTemplateId(""); setAgentId(""); setCampaign(""); setMode("now"); setAt("");
      onComplete?.();
    },
    onError: (err: any) => toast.danger("Launch failed", err.message || "Could not launch campaign"),
  });

  const valid =
    !!batchId && !!instanceName &&
    (messageKind === "template" ? !!templateId : !!agentId) &&
    (mode === "now" || (!!at && new Date(at) > new Date()));

  return (
    <section className="space-y-6">


      {initialPreset?.campaign && (
        <div className="flex items-center gap-2 rounded-lg border border-brand/30 bg-brand-soft/40 px-3.5 py-2 text-xs text-brand">
          <span className="font-bold">Re-running Campaign:</span>
          <span className="font-semibold text-ink">{initialPreset.campaign}</span>
          <span className="text-ink-3">· Settings pre-filled from previous run. Choose your contact batch and schedule.</span>
        </div>
      )}

      {!onCancel && (
        <div>
          <h2 className="text-sm font-semibold text-ink">Schedule Campaign</h2>
          <p className="mt-1 text-xs text-ink-3">Pick a contact batch (or upload Excel), attach a template or AI agent, choose the sending number and time.</p>
        </div>
      )}

      {/* 1. Contacts & batch */}
      <div className="space-y-4 rounded-2xl border border-line bg-elevated p-4 shadow-sm sm:p-6">
        <h3 className="mb-4 text-base font-bold uppercase tracking-wider text-ink-3">1. Contacts &amp; Batch</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <span className={label}>Contact Batch</span>
            <Dropdown
              ariaLabel="Contact Batch"
              dropDown
              value={batchId}
              options={[
                { value: "", label: "Select batch…" },
                ...waBatches.filter((b: any) => !!b.batch_id).map((b: any) => ({
                  value: String(b.batch_id),
                  label: `${b.name} (${b.valid_contacts ?? "?"} contacts)`,
                })),
              ]}
              onChange={setBatchId}
            />
            {batches.isSuccess && waBatches.length === 0 && (
              <p className="mt-1 text-[11px] text-ink-3">No WhatsApp batches yet — upload an Excel below.</p>
            )}
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-line bg-elevated px-3 py-2 text-sm font-semibold text-ink hover:bg-soft"
            >
              <UploadCloud className="h-4 w-4" /> Upload from Excel
            </button>
          </div>
        </div>
      </div>

      {/* 2. Message */}
      <div className="space-y-4 rounded-2xl border border-line bg-elevated p-4 shadow-sm sm:p-6">
        <h3 className="mb-4 text-base font-bold uppercase tracking-wider text-ink-3">2. Template / AI Agent</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {([["template", "Send Template", "Send a saved template"], ["ai_agent", "Attach AI Agent", "Let the agent chat"]] as const).map(([id, t, sub]) => (
            <button
              key={id}
              type="button"
              onClick={() => setMessageKind(id)}
              className={cn("rounded-xl border p-4 text-left", messageKind === id ? "border-accent-500 bg-accent-50 dark:bg-accent-500/10" : "border-line hover:bg-soft")}
            >
              <p className={cn("font-bold", messageKind === id ? "text-accent-700 dark:text-accent-400" : "text-ink")}>{t}</p><p className="text-xs text-ink-3">{sub}</p>
            </button>
          ))}
        </div>
        {messageKind === "template" ? (
          <div>
            <span className={label}>Template (Meta-approved only)</span>
            <Dropdown
              ariaLabel="Template"
              dropDown
              value={templateId}
              options={[
                { value: "", label: approvedTemplates.length === 0 ? "No approved template yet…" : "Select template…" },
                ...approvedTemplates.map(t => ({ value: t.template_id, label: t.name })),
              ]}
              onChange={(id) => {
                setTemplateId(id);
                // A template is approved per Business Account: default the
                // sender to the number it was approved on (still changeable
                // to any number on the same account).
                const picked = approvedTemplates.find(t => t.template_id === id);
                const sender = (picked?.submitted_instance_name || "").trim();
                if (sender && (instances.data ?? []).some(c => c.instance_name === sender)) {
                  setInstanceName(sender);
                }
              }}
            />
            {templates.isSuccess && approvedTemplates.length === 0 && (
              <p className="mt-1 text-[11px] text-warning">No approved template yet — submit one under WhatsApp → Template first.</p>
            )}
            {(() => {
              const picked = approvedTemplates.find(t => t.template_id === templateId);
              if (!picked) return null;
              if (senderForTemplate && senderForTemplate !== instanceName) {
                return (
                  <p className="mt-1 text-[11px] text-ink-3">
                    Approved on <code className="rounded bg-soft px-1 font-mono">{senderForTemplate}</code> — the sender defaults to it; any number on the same Business Account works.
                  </p>
                );
              }
              return null;
            })()}
          </div>
        ) : (
          <div>
            <span className={label}>AI Agent</span>
            <Dropdown
              ariaLabel="AI Agent"
              dropDown
              value={agentId}
              options={[
                { value: "", label: "Select agent…" },
                ...(agents.data ?? []).filter(a => !!a.id).map(a => ({ value: a.id, label: a.name || a.id })),
              ]}
              onChange={setAgentId}
            />
            {(() => {
              const picked = (agents.data ?? []).find(a => a.id === agentId);
              if (!picked) return null;
              const first = (picked.first_message || "").trim();
              if (!first) {
                return (
                  <p className="mt-1 text-[11px] text-warning">
                    This agent has no first message — add one in Agents before launching.
                  </p>
                );
              }
              return null;
            })()}
          </div>
        )}
        <div>
          <span className={label}>Campaign</span>
          <input
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            placeholder="Type campaign name..."
            className="w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      {/* 3. Sender & timing */}
      <div className="space-y-4 rounded-2xl border border-line bg-elevated p-4 shadow-sm sm:p-6">
        <h3 className="mb-4 text-base font-bold uppercase tracking-wider text-ink-3">3. Sender &amp; Timing</h3>
        <div>
          <span className={label}>WhatsApp Instance</span>
          <Dropdown
            ariaLabel="WhatsApp Instance"
            dropDown
            value={instanceName}
            options={[
              { value: "", label: "Select number…" },
              ...(instances.data ?? []).filter(c => !!c.instance_name).map(c => ({
                value: c.instance_name,
                label: c.phone_number ? `+${c.phone_number} · ${c.instance_name}` : c.instance_name,
              })),
            ]}
            onChange={setInstanceName}
          />
          {instances.isSuccess && (instances.data ?? []).length === 0 && (
            <p className="mt-1 text-[11px] text-ink-3">No WhatsApp numbers connected — connect one under Agents first.</p>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {([["now", "Run Now", "Start sending as soon as created"], ["later", "Target Time", "Pick date & time"]] as const).map(([id, t, sub]) => (
            <button
              key={id}
              type="button"
              onClick={() => setMode(id)}
              className={cn("rounded-xl border p-4 text-left", mode === id ? "border-brand bg-brand-soft/30" : "border-line hover:bg-soft")}
            >
              <p className="font-bold text-ink">{t}</p><p className="text-xs text-ink-3">{sub}</p>
            </button>
          ))}
        </div>
        {mode === "later" && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className={label}>Send at</span>
              <DateTimePicker
                value={at}
                onChange={setAt}
                placeholder="Pick target date & time"
                minDate={new Date().toISOString().slice(0, 10)}
                className={cn("w-full", past && "[&>button]:border-danger/50")}
              />
              {past && <span className="mt-1 block text-xs text-danger">Must be in the future.</span>}
            </label>
            <div>
              <span className={label}>Timezone</span>
              <Dropdown ariaLabel="Timezone" dropDown value={timezone} options={[{ value: "Asia/Kolkata", label: "Asia/Kolkata (IST)" }, { value: "UTC", label: "UTC" }]} onChange={setTimezone} />
            </div>
          </div>
        )}
        <div>
          <span className={label}>Concurrency</span>
          <input
            type="number"
            value={concurrency}
            onChange={(e) => setConcurrency(parseInt(e.target.value, 10) || 1)}
            min={1}
            max={200}
            className="w-full max-w-[200px] rounded-lg border border-line px-3 py-2 text-sm focus:border-brand focus:outline-none"
          />
          <p className="mt-1 text-[10px] text-ink-3">How many messages to send in parallel.</p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-paper px-4 py-2.5 text-xs font-semibold text-ink shadow-2xs transition hover:bg-soft dark:bg-[#1f1c1a]"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={() => launch.mutate()}
          disabled={!valid || launch.isPending}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-xs font-semibold text-white shadow-xs transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {launch.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{mode === "later" ? "Scheduling…" : "Launching…"}</span>
            </>
          ) : (
            <span>{mode === "later" ? "Schedule Campaign" : "Launch Campaign"}</span>
          )}
        </button>
      </div>

      {showUpload && <UploadContactBatchModal source="whatsapp" onClose={() => { setShowUpload(false); batches.refetch(); }} />}
    </section>
  );
}
